import { useEffect, useState } from "react";
import { useRouter } from "next/router";

// The remainder of custom.js, rewritten without jQuery. These enhance markup
// that came across from the PHP pages as-is (18 pages carry .accordion FAQs,
// most carry .odometer counters), so they attach by selector on mount rather
// than requiring every page's JSX to be restructured.
//
// Deliberately dropped from custom.js:
//   - the ThemeForest "Buy Now" affiliate button it appended to <body>
//   - the light/dark switch-box it appended to <body> (nothing styled it)
//   - a countdown timer hardcoded to December 2022
//   - the Mailchimp newsletter form, whose markup was commented out
//   - the .search-toggle handler, whose markup was commented out

/**
 * FAQ accordions. The pages use Bootstrap 5's collapse markup
 * (data-bs-toggle="collapse" / .accordion-collapse / .show), which the legacy
 * site drove with bootstrap.bundle.min.js. That bundle is not loaded any more,
 * so the toggling is reimplemented here against the same classes and ARIA
 * attributes — bootstrap.min.css still supplies the styling.
 *
 * Note this is Bootstrap collapse, not the .faq-head accordion custom.js also
 * defined; no page ever carried .faq-head markup.
 */
function useFaqAccordion() {
  const router = useRouter();
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const button = (e.target as HTMLElement).closest<HTMLElement>('[data-bs-toggle="collapse"]');
      if (!button) return;

      const selector = button.getAttribute("data-bs-target") ?? button.getAttribute("href");
      const panel = selector ? document.querySelector<HTMLElement>(selector) : null;
      if (!panel) return;

      e.preventDefault();
      const isOpen = panel.classList.contains("show");

      // Accordions are single-open: close siblings under the same parent.
      const parentSel = panel.getAttribute("data-bs-parent");
      const parent = parentSel ? document.querySelector<HTMLElement>(parentSel) : null;
      if (parent && !isOpen) {
        parent.querySelectorAll<HTMLElement>(".accordion-collapse.show").forEach((other) => {
          if (other === panel) return;
          other.classList.remove("show");
          parent
            .querySelectorAll<HTMLElement>(`[data-bs-target="#${other.id}"]`)
            .forEach((b) => {
              b.classList.add("collapsed");
              b.setAttribute("aria-expanded", "false");
            });
        });
      }

      panel.classList.toggle("show", !isOpen);
      button.classList.toggle("collapsed", isOpen);
      button.setAttribute("aria-expanded", String(!isOpen));
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [router.asPath]);
}

/** .odometer counters — custom.js just wrote data-count in on appear. */
function useOdometers() {
  const router = useRouter();
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll<HTMLElement>(".odometer"));
    if (!nodes.length) return;

    const animate = (el: HTMLElement) => {
      const target = Number(el.getAttribute("data-count") ?? el.textContent ?? 0);
      if (!Number.isFinite(target)) return;
      const duration = 1500;
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min((now - start) / duration, 1);
        // ease-out so the number settles rather than stopping dead
        el.textContent = String(Math.round(target * (1 - Math.pow(1 - t, 3))));
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            animate(entry.target as HTMLElement);
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.25 }
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [router.asPath]);
}

/** WOW.js replacement: reveal .wow elements as they scroll into view. */
function useScrollReveal() {
  const router = useRouter();
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll<HTMLElement>(".wow"));
    if (!nodes.length) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      nodes.forEach((n) => (n.style.visibility = "visible"));
      return;
    }

    nodes.forEach((n) => {
      n.style.visibility = "hidden";
    });

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          el.style.visibility = "visible";
          const delay = el.getAttribute("data-wow-delay");
          if (delay) el.style.animationDelay = delay;
          el.classList.add("animated");
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [router.asPath]);
}

/** The #toTop button custom.js appended to <body>. */
function BackToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 0);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <button
      type="button"
      id="toTop"
      className="back-to-top-btn"
      aria-label="Back to top"
      style={{ display: visible ? "block" : "none" }}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    >
      <i className="icofont-hand-drawn-up" />
    </button>
  );
}

/**
 * magnificPopup replacement for .popup-youtube links. Intercepts the click,
 * pulls the video id out of the href and opens it in an mfp-classed overlay so
 * magnific-popup.min.css still styles the chrome.
 */
function VideoPopup() {
  const [src, setSrc] = useState<string | null>(null);
  const router = useRouter();

  useEffect(() => {
    const links = Array.from(document.querySelectorAll<HTMLAnchorElement>(".popup-youtube"));
    const onClick = (e: MouseEvent) => {
      const href = (e.currentTarget as HTMLAnchorElement).href;
      const id = href.match(/(?:youtu\.be\/|[?&]v=|embed\/|shorts\/)([\w-]{6,20})/)?.[1];
      if (!id) return;
      e.preventDefault();
      setSrc(`https://www.youtube.com/embed/${id}?autoplay=1`);
    };
    links.forEach((l) => l.addEventListener("click", onClick));
    return () => links.forEach((l) => l.removeEventListener("click", onClick));
  }, [router.asPath]);

  useEffect(() => {
    if (!src) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setSrc(null);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [src]);

  if (!src) return null;

  return (
    <div className="mfp-bg mfp-fade mfp-ready" onClick={() => setSrc(null)}>
      <div className="mfp-wrap mfp-close-btn-in mfp-auto-cursor mfp-fade mfp-ready" tabIndex={-1}>
        <div className="mfp-container mfp-iframe-holder">
          <div className="mfp-content" onClick={(e) => e.stopPropagation()}>
            <div className="mfp-iframe-scaler">
              <button type="button" className="mfp-close" aria-label="Close video" onClick={() => setSrc(null)}>
                ×
              </button>
              <iframe
                className="mfp-iframe"
                src={src}
                title="Video"
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Click-to-load YouTube facades. video.php had 36 copies of
 * onclick="loadYouTubeVideo(this)"; this delegates from the document instead,
 * reading data-video-id / data-height off the wrapper exactly as before.
 */
function useYouTubeFacades() {
  const router = useRouter();
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const thumb = (e.target as HTMLElement).closest<HTMLElement>(".thumbnail");
      if (!thumb) return;
      const wrapper = thumb.parentElement;
      const videoId = wrapper?.getAttribute("data-video-id");
      if (!wrapper || !videoId) return;

      e.preventDefault();
      const iframe = document.createElement("iframe");
      iframe.width = "100%";
      iframe.height = wrapper.getAttribute("data-height") ?? "400";
      iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1`;
      iframe.frameBorder = "0";
      iframe.allowFullscreen = true;
      iframe.allow =
        "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
      wrapper.replaceChild(iframe, thumb);
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [router.asPath]);
}

/** img.lazy-load -> swap data-src in on view. Was an inline script on index.php. */
function useLazyImages() {
  const router = useRouter();
  useEffect(() => {
    const images = Array.from(document.querySelectorAll<HTMLImageElement>("img.lazy-load"));
    if (!images.length) return;

    const observer = new IntersectionObserver((entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const img = entry.target as HTMLImageElement;
        if (img.dataset.src) img.src = img.dataset.src;
        img.classList.remove("lazy-load");
        obs.unobserve(img);
      }
    });
    images.forEach((img) => observer.observe(img));
    return () => observer.disconnect();
  }, [router.asPath]);
}

export default function LegacyBehaviours() {
  useFaqAccordion();
  useOdometers();
  useScrollReveal();
  useYouTubeFacades();
  useLazyImages();
  return (
    <>
      <BackToTop />
      <VideoPopup />
    </>
  );
}
